import { makeAutoObservable, runInAction } from 'mobx';
import CustomersService from '../../../../services/api/customer-service';
import OrderService from '../../../../services/order-service';
import { rootContainer, TYPE } from '../../../../services/container/root';
import { LOAD_STATE, LoadState } from '../../../../types/load-state';

/**
 * Shopper name and shipping address of a single RMA, fetched on demand.
 * These fields are excluded from the RMA page payloads, so each RMA view loads them separately.
 */
class RmaShopperDetailsStore {
    private _nameLoadState: LoadState = LOAD_STATE.IDLE;
    private _firstName?: string | null;
    private _lastName?: string | null;
    private _customerURL?: string;

    private _shippingAddressLoadState: LoadState = LOAD_STATE.IDLE;
    private _shippingAddress?: string;
    private _updatedShippingAddress?: string;

    constructor(private readonly _rmaId: number) {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    private get _customersService(): CustomersService {
        return rootContainer.get<CustomersService>(TYPE.CustomersService);
    }

    private get _orderService(): OrderService {
        return rootContainer.get<OrderService>(TYPE.OrderService);
    }

    public get nameLoadState(): LoadState { return this._nameLoadState; }
    public get shippingAddressLoadState(): LoadState { return this._shippingAddressLoadState; }
    public get customerURL(): string | undefined { return this._customerURL; }
    public get shippingAddress(): string | undefined { return this._shippingAddress; }
    public get updatedShippingAddress(): string | undefined { return this._updatedShippingAddress; }

    public get fullName(): string | undefined {
        if (!this._firstName && !this._lastName) {
            return undefined;
        }
        return `${this._firstName || ''} ${this._lastName || ''}`.trim();
    }

    public async loadName(): Promise<void> {
        if (this._nameLoadState === LOAD_STATE.LOADING || this._nameLoadState === LOAD_STATE.LOADED) {
            return;
        }

        this._nameLoadState = LOAD_STATE.LOADING;
        try {
            const namePayload = await this._customersService.loadCustomerName(this._rmaId);
            runInAction(() => {
                this._firstName = namePayload.firstName;
                this._lastName = namePayload.lastName;
                this._customerURL = namePayload.customerURL;
                this._nameLoadState = LOAD_STATE.LOADED;
            });
        } catch (e) {
            runInAction(() => {
                this._nameLoadState = LOAD_STATE.FAILED;
            });
            console.error('Error loading shopper name', e);
        }
    }

    public async loadShopperAddress(): Promise<void> {
        if (this._shippingAddressLoadState === LOAD_STATE.LOADING
            || this._shippingAddressLoadState === LOAD_STATE.LOADED) {
            return;
        }

        this._shippingAddressLoadState = LOAD_STATE.LOADING;
        try {
            const response = await this._orderService.loadShopperAddress(this._rmaId);
            runInAction(() => {
                this._shippingAddress = response.shippingAddress ?? undefined;
                this._updatedShippingAddress = response.updatedShippingAddress ?? undefined;
                this._shippingAddressLoadState = LOAD_STATE.LOADED;
            });
        } catch (e) {
            runInAction(() => {
                this._shippingAddressLoadState = LOAD_STATE.FAILED;
            });
            console.error('Error loading shipping address', e);
        }
    }
}

export default RmaShopperDetailsStore;
